import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
	Paper,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	Stack,
} from '@mui/material'
import * as React from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ConstructionIcon from '@mui/icons-material/Construction'
import UndoIcon from '@mui/icons-material/Undo'
import CheckIcon from '@mui/icons-material/Check'
import { ShiftEndTaskSummary } from '../../../../types/Reports/ShiftEnd/ShiftEndWorkItems'
import { getTaskMaterialDemands } from '../../../../services/materialService'
import { TaskMaterialDemands } from '../../../../types/Material/Material'
import {
	createBuildSheet,
	getTaskLocation,
	updateTaskLocation,
} from '../../../../services/scaffoldRequestService'
import { getCurrentLocation } from '../../../../utils/LocationUtils'
import { showErrorToast } from '../../../../services/CommonServices/alertServices'

type BuildSheetDialogProps = {
	open: boolean
	onClose: () => void
	task: ShiftEndTaskSummary
	workItemTaskId: number
	postSubmit: () => void
}

type BuildSheetData = {
	width: number | null
	height: number | null
	length: number | null
	decks: number | null
	legs: number | null
	notes: string
	latitude: number | null
	longitude: number | null
}

export const BuildSheetDialog = ({
	open,
	onClose,
	task,
	workItemTaskId,
	postSubmit,
}: BuildSheetDialogProps) => {
	const [buildSheetData, setBuildSheetData] = React.useState<BuildSheetData>({
		width: null,
		height: null,
		length: null,
		decks: null,
		legs: null,
		notes: '',
		latitude: null,
		longitude: null,
	})
	const [materialDemands, setMaterialDemands] = React.useState<TaskMaterialDemands[]>([])
	const [usedQuantities, setUsedQuantities] = React.useState<{ [key: number]: number | null }>({})
	const [loadingMaterialRequests, setLoadingMaterialRequests] = React.useState(false)
	const [submittingBuildSheet, setSubmittingBuildSheet] = React.useState(false)
	const [locationChanged, setLocationChanged] = React.useState(false)
	const [findingLocation, setFindingLocation] = React.useState(false)

	React.useEffect(() => {
		if (open) {
			loadMaterialRequests()
			loadTaskLocation()
		}
	}, [open, workItemTaskId])

	const loadMaterialRequests = async () => {
		setLoadingMaterialRequests(true)
		try {
			const data = await getTaskMaterialDemands(workItemTaskId)
			setMaterialDemands(data)
			setUsedQuantities(Object.fromEntries(data.map((demand) => [demand.inventoryId, null])))
		} finally {
			setLoadingMaterialRequests(false)
		}
	}

	const loadTaskLocation = async () => {
		try {
			const location = await getTaskLocation(workItemTaskId)
			if (location) {
				setBuildSheetData((prev) => ({
					...prev,
					latitude: location.latitude,
					longitude: location.longitude,
				}))
			}
		} catch (err) {
			console.error('Error loading task location:', err)
		}
	}

	const handleUsedQuantityChange = (inventoryId: number, value: string) => {
		if (value === '') {
			setUsedQuantities((prev) => ({
				...prev,
				[inventoryId]: null,
			}))
			return
		}
		// Whole numbers only (reject decimals and other characters)
		if (!/^\d+$/.test(value)) {
			return
		}
		setUsedQuantities((prev) => ({
			...prev,
			[inventoryId]: parseInt(value, 10),
		}))
	}

	const handleUseAll = () => {
		setUsedQuantities(
			Object.fromEntries(
				materialDemands.map((demand) => [
					demand.inventoryId,
					Math.max(0, Math.floor(demand.receivedQuantity ?? 0)),
				]),
			),
		)
	}

	const handleReturnAll = () => {
		setUsedQuantities(Object.fromEntries(materialDemands.map((demand) => [demand.inventoryId, 0])))
	}

	const calculateReturningQuantity = (demand: TaskMaterialDemands): number => {
		const usedQty = usedQuantities[demand.inventoryId] || 0
		return Math.max(0, (demand.receivedQuantity ?? 0) - usedQty)
	}

	const handleInputChange = (field: keyof BuildSheetData, value: string) => {
		if (field === 'notes') {
			setBuildSheetData((prev) => ({
				...prev,
				[field]: value,
			}))
		} else {
			const numericValue = value === '' ? null : parseFloat(value)
			setBuildSheetData((prev) => ({
				...prev,
				[field]: numericValue,
			}))

			// Track if location fields are changed
			if (field === 'latitude' || field === 'longitude') {
				setLocationChanged(true)
			}
		}
	}

	const handleSubmit = async () => {
		setSubmittingBuildSheet(true)
		let success = false
		try {
			// Update location if it was changed
			if (
				locationChanged &&
				buildSheetData.latitude !== null &&
				buildSheetData.longitude !== null
			) {
				await updateTaskLocation(workItemTaskId, {
					latitude: buildSheetData.latitude,
					longitude: buildSheetData.longitude,
				})
			}

			success = await createBuildSheet(
				{
					...buildSheetData,
					quantityUsed: Object.fromEntries(
						Object.entries(usedQuantities).map(([key, value]) => [key, value ?? 0]),
					),
				},
				task.id,
			)
		} finally {
			setSubmittingBuildSheet(false)
			onClose()
		}

		if (success) {
			postSubmit()
		}
	}

	const isFormValid = () => {
		const measurementsValid =
			buildSheetData.width !== null &&
			buildSheetData.height !== null &&
			buildSheetData.length !== null &&
			buildSheetData.decks !== null &&
			buildSheetData.legs !== null &&
			buildSheetData.width > 0 &&
			buildSheetData.height > 0 &&
			buildSheetData.length > 0 &&
			buildSheetData.decks > 0 &&
			buildSheetData.legs > 0 &&
			buildSheetData.latitude !== null &&
			buildSheetData.longitude !== null
		const materialQuantitiesValid = materialDemands.every((demand) => {
			const usedQty = usedQuantities[demand.inventoryId]
			return usedQty !== null && usedQty !== undefined && usedQty >= 0
		})
		return measurementsValid && materialQuantitiesValid
	}

	const handleFindLocation = async () => {
		setFindingLocation(true)
		try {
			const location = await getCurrentLocation()
			setLocationChanged(true)
			setBuildSheetData((prev) => ({
				...prev,
				latitude: location.latitude,
				longitude: location.longitude,
			}))
		} catch (error) {
			showErrorToast({
				message:
					error instanceof Error ? error.message : 'Failed to find accurate location. Try again.',
			})
		} finally {
			setFindingLocation(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='xl'>
			<div
				onClick={(e: React.MouseEvent<HTMLDivElement>) => {
					e.stopPropagation()
				}}
			>
				<DialogTitle>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<ConstructionIcon color='primary' />
						<Typography variant='h6' component='span'>
							Build Sheet - {task.title}
						</Typography>
					</Box>
				</DialogTitle>
				<IconButton
					onClick={onClose}
					sx={(theme) => ({
						position: 'absolute',
						right: 8,
						top: 8,
						color: theme.palette.grey[500],
					})}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minHeight: '60vh' }}>
						{/* Top section - Scaffold Measurements */}
						<Paper
							elevation={2}
							sx={{
								p: 3,
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<Typography
								variant='h6'
								gutterBottom
								sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
							>
								<ConstructionIcon color='primary' />
								Scaffold Measurements
							</Typography>
							<Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
								Enter the final dimensions and specifications for the completed scaffold work.
							</Typography>

							<Box
								sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}
							>
								{/* Dimensions row */}
								<Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
									<TextField
										label='Length'
										type='number'
										value={buildSheetData.length || ''}
										onChange={(e) => handleInputChange('length', e.target.value)}
										InputProps={{
											endAdornment: <Typography variant='body2'>ft</Typography>,
										}}
										inputProps={{
											min: 0,
											step: 0.1,
										}}
										sx={{ flex: 1 }}
									/>
									<TextField
										label='Width'
										type='number'
										value={buildSheetData.width || ''}
										onChange={(e) => handleInputChange('width', e.target.value)}
										InputProps={{
											endAdornment: <Typography variant='body2'>ft</Typography>,
										}}
										inputProps={{
											min: 0,
											step: 0.1,
										}}
										sx={{ flex: 1 }}
									/>
									<TextField
										label='Height'
										type='number'
										value={buildSheetData.height || ''}
										onChange={(e) => handleInputChange('height', e.target.value)}
										InputProps={{
											endAdornment: <Typography variant='body2'>ft</Typography>,
										}}
										inputProps={{
											min: 0,
											step: 0.1,
										}}
										sx={{ flex: 1 }}
									/>
								</Box>

								{/* Decks field */}
								<TextField
									label='Number of Decks'
									type='number'
									value={buildSheetData.decks || ''}
									onChange={(e) => handleInputChange('decks', e.target.value)}
									inputProps={{
										min: 1,
										step: 1,
									}}
									sx={{ width: '100%', maxWidth: 300 }}
								/>

								{/* Legs field */}
								<TextField
									label='Number of Legs'
									type='number'
									value={buildSheetData.legs || ''}
									onChange={(e) => handleInputChange('legs', e.target.value)}
									inputProps={{
										min: 1,
										step: 1,
									}}
									sx={{ width: '100%', maxWidth: 300 }}
								/>
							</Box>
						</Paper>

						{/* Location section */}
						<Paper
							elevation={2}
							sx={{
								p: 3,
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									mb: 1,
								}}
							>
								<Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<ConstructionIcon color='primary' />
									Location
								</Typography>
								<Button
									variant='contained'
									color='primary'
									onClick={handleFindLocation}
									disabled={findingLocation}
									startIcon={
										findingLocation ? (
											<CircularProgress size={16} color='inherit' sx={{ p: 0, m: 0 }} />
										) : null
									}
									size='small'
								>
									{findingLocation ? 'Finding Location...' : 'Find Location'}
								</Button>
							</Box>
							<Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
								Task location coordinates for this completed work.
							</Typography>

							<Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
								<TextField
									label='Latitude'
									type='number'
									value={buildSheetData.latitude || ''}
									onChange={(e) => handleInputChange('latitude', e.target.value)}
									inputProps={{
										min: -90,
										max: 90,
										step: 0.000001,
									}}
									sx={{ flex: 1 }}
								/>
								<TextField
									label='Longitude'
									type='number'
									value={buildSheetData.longitude || ''}
									onChange={(e) => handleInputChange('longitude', e.target.value)}
									inputProps={{
										min: -180,
										max: 180,
										step: 0.000001,
									}}
									sx={{ flex: 1 }}
								/>
							</Box>
						</Paper>

						{/* Bottom section - Material Usage */}
						<Paper
							elevation={2}
							sx={{
								p: 2,
								flex: 1,
								display: 'flex',
								flexDirection: 'column',
								minHeight: '400px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'flex-start',
									gap: 2,
									mb: 2,
								}}
							>
								<Box>
									<Typography variant='h6' gutterBottom sx={{ mb: 0.5 }}>
										Final Material Usage
									</Typography>
									<Typography variant='body2' color='text.secondary'>
										Review and manage the materials used for this completed task.
									</Typography>
								</Box>
								<Stack direction='row' spacing={1} sx={{ flexShrink: 0 }}>
									<Button
										variant='outlined'
										size='small'
										onClick={handleReturnAll}
										disabled={loadingMaterialRequests || materialDemands.length === 0}
										startIcon={<UndoIcon />}
										sx={{
											color: 'error.light',
											borderColor: 'error.light',
										}}
									>
										Return All
									</Button>
									<Button
										variant='outlined'
										size='small'
										onClick={handleUseAll}
										disabled={loadingMaterialRequests || materialDemands.length === 0}
										startIcon={<CheckIcon />}
										sx={{
											color: 'success.main',
											borderColor: 'success.main',
										}}
									>
										Use All
									</Button>
								</Stack>
							</Box>
							<Box sx={{ flex: 1, overflow: 'hidden' }}>
								<TableContainer
									component={Paper}
									elevation={0}
									sx={{
										border: '1px solid #e5e7eb',
										borderRadius: 2,
										maxHeight: 400,
										overflow: 'auto',
									}}
								>
									<Table stickyHeader>
										<TableHead>
											<TableRow sx={{ backgroundColor: '#f9fafb' }}>
												<TableCell
													sx={{
														fontWeight: 600,
														color: '#374151',
														borderBottom: '2px solid #e5e7eb',
														py: 2,
														fontSize: '0.875rem',
													}}
												>
													Item Name
												</TableCell>
												<TableCell
													sx={{
														fontWeight: 600,
														color: '#374151',
														borderBottom: '2px solid #e5e7eb',
														py: 2,
														fontSize: '0.875rem',
													}}
												>
													Unit of Measure
												</TableCell>
												<TableCell
													sx={{
														fontWeight: 600,
														color: '#374151',
														borderBottom: '2px solid #e5e7eb',
														py: 2,
														fontSize: '0.875rem',
														textAlign: 'center',
													}}
												>
													Requested
												</TableCell>
												<TableCell
													sx={{
														fontWeight: 600,
														color: '#374151',
														borderBottom: '2px solid #e5e7eb',
														py: 2,
														fontSize: '0.875rem',
														textAlign: 'center',
													}}
												>
													Received
												</TableCell>
												<TableCell
													sx={{
														fontWeight: 600,
														color: '#374151',
														borderBottom: '2px solid #e5e7eb',
														py: 2,
														fontSize: '0.875rem',
														textAlign: 'center',
													}}
												>
													Used
												</TableCell>
												<TableCell
													sx={{
														fontWeight: 600,
														color: '#374151',
														borderBottom: '2px solid #e5e7eb',
														py: 2,
														fontSize: '0.875rem',
														textAlign: 'center',
													}}
												>
													Returning
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{loadingMaterialRequests ? (
												<TableRow>
													<TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
														<CircularProgress color='success' />
													</TableCell>
												</TableRow>
											) : materialDemands.length === 0 ? (
												<TableRow>
													<TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
														<Typography
															variant='body2'
															sx={{ color: '#6b7280', fontStyle: 'italic' }}
														>
															No material requests found for this task.
														</Typography>
													</TableCell>
												</TableRow>
											) : (
												materialDemands.map((demand) => (
													<TableRow
														key={demand.inventoryId}
														sx={{
															'&:nth-of-type(even)': { backgroundColor: '#fafafa' },
															'&:hover': { backgroundColor: '#f8fafc' },
															transition: 'background-color 0.2s ease',
														}}
													>
														<TableCell sx={{ py: 2.5 }}>
															<Stack direction='row' alignItems='center' spacing={1.5}>
																<Box
																	sx={{
																		width: 8,
																		height: 8,
																		borderRadius: '50%',
																		backgroundColor: '#10b981',
																	}}
																/>
																<Typography
																	variant='body2'
																	sx={{ fontWeight: 500, color: '#111827' }}
																>
																	{demand.name}
																</Typography>
															</Stack>
														</TableCell>
														<TableCell sx={{ py: 2.5 }}>
															<Chip
																label={demand.unitOfMeasure}
																size='small'
																variant='outlined'
																sx={{
																	fontSize: '0.75rem',
																	fontWeight: 500,
																	borderColor: '#d1d5db',
																	color: '#6b7280',
																	backgroundColor: '#f9fafb',
																}}
															/>
														</TableCell>
														<TableCell sx={{ py: 2.5, textAlign: 'center' }}>
															<Typography
																variant='body2'
																sx={{
																	fontWeight: 600,
																	color: '#111827',
																	fontSize: '0.875rem',
																}}
															>
																{demand.requestedQuantity}
															</Typography>
														</TableCell>
														<TableCell sx={{ py: 2.5, textAlign: 'center' }}>
															<Typography
																variant='body2'
																sx={{
																	fontWeight: 600,
																	color: '#111827',
																	fontSize: '0.875rem',
																}}
															>
																{demand.receivedQuantity ?? 0}
															</Typography>
														</TableCell>
														<TableCell sx={{ py: 2.5, textAlign: 'center' }}>
															<TextField
																type='text'
																size='small'
																value={
																	usedQuantities[demand.inventoryId] == null
																		? ''
																		: String(usedQuantities[demand.inventoryId])
																}
																onChange={(e) =>
																	handleUsedQuantityChange(demand.inventoryId, e.target.value)
																}
																inputProps={{
																	inputMode: 'numeric',
																	pattern: '[0-9]*',
																	style: {
																		backgroundColor:
																			(usedQuantities[demand.inventoryId] || 0) >
																			(demand.receivedQuantity || 0)
																				? 'rgba(255,230,222,1)'
																				: 'white',
																	},
																}}
																sx={{
																	width: 120,
																	'& .MuiOutlinedInput-root': {
																		borderRadius: 1.5,
																		fontSize: '0.875rem',
																		'&:hover fieldset': {
																			borderColor: '#3b82f6',
																		},
																		'&.Mui-focused fieldset': {
																			borderColor: '#3b82f6',
																			borderWidth: 2,
																		},
																	},
																}}
															/>
														</TableCell>
														<TableCell sx={{ py: 2.5, textAlign: 'center' }}>
															<Box
																sx={{
																	display: 'inline-flex',
																	alignItems: 'center',
																	px: 2,
																	py: 1,
																	backgroundColor: '#ecfdf5',
																	borderRadius: 1.5,
																	border: '1px solid #10b981',
																	minWidth: 60,
																	justifyContent: 'center',
																}}
															>
																<Typography
																	variant='body2'
																	sx={{
																		fontWeight: 700,
																		color: '#065f46',
																		fontSize: '0.875rem',
																	}}
																>
																	{calculateReturningQuantity(demand)}
																</Typography>
															</Box>
														</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						</Paper>

						{/* Notes Section */}
						<Paper
							elevation={2}
							sx={{
								p: 3,
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<Typography variant='h6' gutterBottom>
								Notes
							</Typography>
							<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
								Add any additional notes or comments about the completed work.
							</Typography>
							<TextField
								multiline
								rows={4}
								value={buildSheetData.notes}
								onChange={(e) => handleInputChange('notes', e.target.value)}
								placeholder='Enter any additional notes or comments...'
								sx={{
									'& .MuiOutlinedInput-root': {
										borderRadius: 2,
										'&:hover fieldset': {
											borderColor: '#3b82f6',
										},
										'&.Mui-focused fieldset': {
											borderColor: '#3b82f6',
											borderWidth: 2,
										},
									},
								}}
							/>
						</Paper>
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
					<Button
						color='warning'
						variant='contained'
						onClick={onClose}
						disabled={submittingBuildSheet}
					>
						Cancel
					</Button>
					{submittingBuildSheet ? (
						<CircularProgress color='success' sx={{ m: 0, p: 0 }} />
					) : (
						<Button
							color='success'
							variant='contained'
							onClick={handleSubmit}
							disabled={!isFormValid() || submittingBuildSheet}
						>
							Complete Build Sheet
						</Button>
					)}
				</DialogActions>
			</div>
		</Dialog>
	)
}
